<template>
  <div v-if="visible" class="d-flex align-items-center">
    <button type="button" class="btn btn-icon btn-light-primary shadow-sm w-30px h-30px w-md-40px h-md-40px"
            :title="$t('pwa_install.button')" @click="install">
      <i class="bi bi-download fs-3"></i>
    </button>
  </div>

  <!-- iPhone / iPad : Safari ne propose pas d'installation en un clic, on montre les gestes -->
  <teleport to="body">
    <div v-if="iosGuide" class="pwa-guide-backdrop" @click.self="iosGuide = false">
      <div class="pwa-guide card">
        <div class="card-body text-center">
          <img src="/img/icons/android-chrome-192x192.png" alt="Qomerxia" class="pwa-guide-icon mb-4" />
          <h3 class="fw-bolder mb-2">{{ $t('pwa_install.ios_title') }}</h3>
          <p class="text-muted mb-6">{{ $t('pwa_install.ios_text') }}</p>

          <ol class="pwa-guide-steps text-start">
            <li>
              <span class="pwa-guide-num">1</span>
              <span>
                {{ $t('pwa_install.ios_step1') }}
                <svg class="pwa-guide-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
                </svg>
              </span>
            </li>
            <li>
              <span class="pwa-guide-num">2</span>
              <span>
                {{ $t('pwa_install.ios_step2') }}
                <svg class="pwa-guide-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="4" y="4" width="16" height="16" rx="3" /><path d="M12 8v8M8 12h8" />
                </svg>
              </span>
            </li>
            <li>
              <span class="pwa-guide-num">3</span>
              <span>{{ $t('pwa_install.ios_step3') }}</span>
            </li>
          </ol>

          <p class="text-muted fs-7 mb-5">{{ $t('pwa_install.ios_safari') }}</p>
          <button type="button" class="btn btn-sm btn-primary" @click="iosGuideDone">{{ $t('pwa_install.got_it') }}</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
/**
 * Bouton « Installer l'application » de l'en-tête : visible seulement si l'installation est
 * possible (événement du navigateur capturé dans main.js) ou sur iPhone / iPad, et jamais
 * dans l'application déjà installée. Pas de fenêtre qui s'ouvre d'elle-même.
 */
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

// iPhone / iPad : Safari ne dit pas si l'application est déjà sur l'écran d'accueil ; après
// « J'ai compris » dans le guide, on ne remontre plus le bouton dans ce navigateur
const IOS_GUIDE_DONE_KEY = 'pwa.iosGuideDone'
function iosGuideAlreadyDone() {
  try { return localStorage.getItem(IOS_GUIDE_DONE_KEY) === '1' } catch (e) { return false }
}

function isIos() {
  const ua = navigator.userAgent
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

export default {
  name: 'PwaInstallButton',
  data() {
    return {
      available: !!window.__pwaInstallEvent,
      installed: isStandalone(),
      ios: isIos() && !iosGuideAlreadyDone(),
      iosGuide: false,
    }
  },
  computed: {
    visible() {
      return !this.installed && (this.available || this.ios)
    },
  },
  mounted() {
    window.addEventListener('pwa-install-available', this.onAvailable)
    window.addEventListener('pwa-installed', this.onInstalled)
  },
  beforeUnmount() {
    window.removeEventListener('pwa-install-available', this.onAvailable)
    window.removeEventListener('pwa-installed', this.onInstalled)
  },
  methods: {
    iosGuideDone() {
      this.iosGuide = false
      this.ios = false
      try { localStorage.setItem(IOS_GUIDE_DONE_KEY, '1') } catch (e) { /* stockage indisponible */ }
    },
    onAvailable() {
      this.available = true
    },
    onInstalled() {
      this.installed = true
      this.available = false
    },
    async install() {
      const prompt = window.__pwaInstallEvent
      if (!prompt) {
        if (this.ios) this.iosGuide = true
        return
      }
      prompt.prompt()
      const { outcome } = await prompt.userChoice
      // l'événement ne sert qu'une fois ; le navigateur le renverra plus tard si refusé
      window.__pwaInstallEvent = null
      this.available = false
      if (outcome === 'accepted') this.installed = true
    },
  },
}
</script>

<style scoped>
.pwa-guide-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.pwa-guide {
  width: 100%;
  max-width: 380px;
}

.pwa-guide-icon {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
}

.pwa-guide-steps {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pwa-guide-steps li {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pwa-guide-num {
  flex: 0 0 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--kt-primary);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pwa-guide-glyph {
  width: 20px;
  height: 20px;
  vertical-align: -4px;
  margin-left: 2px;
  color: #007aff;
}
</style>
